import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { i18n } from '../../translate/i18n';
import { SocketContext } from '../../context/Socket/SocketContext';

const TicketResponseTimeIndicator = ({ ticketId, ticketStatus }) => {
  const theme = useTheme();
  const socketManager = useContext(SocketContext);

  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState(null);
  const [nowTs, setNowTs] = useState(() => Date.now());

  const isClosed = ticketStatus === 'closed';

  const formatDurationMs = (ms, { showSeconds } = { showSeconds: false }) => {
    const safeMs = typeof ms === 'number' && ms > 0 ? ms : 0;
    const totalSeconds = Math.floor(safeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    if (showSeconds) return `${hh}h ${mm}m ${ss}s`;
    return `${hh}h ${mm}m`;
  };

  const fetchAllMessagesForTicket = async currentTicketId => {
    if (!currentTicketId) {
      setMessages([]);
      return;
    }

    setMessagesLoading(true);
    setMessagesError(null);

    const MAX_PAGES = 50;
    let pageNumber = 1;
    let hasMore = true;
    let loops = 0;
    const all = [];

    try {
      while (hasMore && loops < MAX_PAGES) {
        const { data } = await api.get(`/messages/${currentTicketId}`, {
          params: { pageNumber },
        });

        const batch = Array.isArray(data?.messages) ? data.messages : [];
        all.push(...batch);

        hasMore = Boolean(data?.hasMore);
        pageNumber += 1;
        loops += 1;

        if (batch.length === 0) break;
      }
    } catch (err) {
      setMessagesError(i18n.t('tickets.responseTime.errors.loadMessages'));
      toastError(err);
    } finally {
      const byId = new Map();
      all.forEach(m => {
        if (!m) return;
        const key =
          m.id ?? `${m.ticketId ?? 't'}-${m.createdAt ?? ''}-${m.fromMe ?? ''}`;
        byId.set(key, m);
      });

      const unique = Array.from(byId.values());
      unique.sort((a, b) => {
        const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return ta - tb;
      });

      setMessages(unique);
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    if (!ticketId) {
      setMessages([]);
      setMessagesLoading(false);
      setMessagesError(null);
      return;
    }

    fetchAllMessagesForTicket(ticketId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const responseStats = useMemo(() => {
    let waitingStartMs = null;
    let sumMs = 0;
    let count = 0;

    (messages || []).forEach(msg => {
      const ts = msg?.createdAt ? new Date(msg.createdAt).getTime() : null;
      if (!ts || Number.isNaN(ts)) return;

      const fromMe = msg?.fromMe;
      if (fromMe === false) {
        if (waitingStartMs == null) waitingStartMs = ts;
        return;
      }

      if (fromMe === true) {
        if (waitingStartMs != null) {
          const delta = ts - waitingStartMs;
          if (delta >= 0) {
            sumMs += delta;
            count += 1;
          }
          waitingStartMs = null;
        }
      }
    });

    const avgMs = count > 0 ? Math.round(sumMs / count) : 0;

    return { avgMs, count, waitingStartMs };
  }, [messages]);

  const isWaitingForAgent =
    !isClosed &&
    responseStats.waitingStartMs != null &&
    responseStats.waitingStartMs > 0;

  const waitingMs = isWaitingForAgent ? nowTs - responseStats.waitingStartMs : 0;

  useEffect(() => {
    if (!isWaitingForAgent) return;
    const interval = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isWaitingForAgent]);

  useEffect(() => {
    if (!ticketId) return;
    if (!socketManager) return;

    const companyId = localStorage.getItem('companyId');
    if (!companyId) return;

    const socket = socketManager.getSocket(companyId);

    const handleReady = () => {
      try {
        socket.emit('joinChatBox', `${ticketId}`);
      } catch {
        // ignore
      }
    };

    const messageEvent = `company-${companyId}-appMessage`;
    const handleMessageEvent = data => {
      if (!data || !data.message) return;
      if (data.message.ticketId !== ticketId) return;

      if (data.action === 'create') {
        setMessages(prev => {
          const exists = prev.some(m => m?.id != null && m.id === data.message.id);
          const next = exists
            ? prev.map(m => (m?.id === data.message.id ? data.message : m))
            : [...prev, data.message];
          next.sort((a, b) => {
            const ta = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
            const tb = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
            return ta - tb;
          });
          return next;
        });
      }

      if (data.action === 'update') {
        setMessages(prev =>
          prev.map(m => (m?.id === data.message.id ? data.message : m))
        );
      }
    };

    socket.on('ready', handleReady);
    socket.on(messageEvent, handleMessageEvent);

    return () => {
      socket.off('ready', handleReady);
      socket.off(messageEvent, handleMessageEvent);
    };
  }, [socketManager, ticketId]);

  // Só mostrar o que foi solicitado: aguardando resposta e, ao final, tempo médio.
  if (!ticketId) return null;
  if (!isWaitingForAgent && !isClosed) return null;

  const captionStyle = {
    display: 'block',
    fontSize: 12,
    marginTop: 4,
    color: theme.palette.text.secondary,
  };

  return (
    <>
      {isWaitingForAgent && (
        <Typography style={captionStyle}>
          {i18n.t('tickets.responseTime.waitingLabel')}:&nbsp;
          {messagesLoading
            ? '...'
            : formatDurationMs(waitingMs, { showSeconds: true })}
          {messagesError ? ` (${messagesError})` : ''}
        </Typography>
      )}

      {isClosed && (
        <Typography style={captionStyle}>
          {i18n.t('tickets.responseTime.avgLabel')}:&nbsp;
          {messagesLoading
            ? '...'
            : formatDurationMs(responseStats.avgMs, { showSeconds: false })}
          {messagesError ? ` (${messagesError})` : ''}
        </Typography>
      )}
    </>
  );
};

export default TicketResponseTimeIndicator;

